import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { api, ApiError } from './api/client';
import type { Me } from './api/types';
import { MeContext, useMe } from './me';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import AgentTokensPage from './pages/AgentTokensPage';
import AuditPage from './pages/AuditPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import UploadPage from './pages/UploadPage';

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* #15: unauthed one-time upload page — must sit OUTSIDE the
          Authed shell so an external party (no session) can use it. */}
      <Route path="/u/:token" element={<UploadPage />} />
      <Route
        path="/*"
        element={
          <Authed>
            <AppShell />
          </Authed>
        }
      />
    </Routes>
  );
}

function Authed({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ loading: boolean; me: Me | null }>({
    loading: true,
    me: null,
  });
  useEffect(() => {
    api<Me>('/me')
      .then((me) => setState({ loading: false, me }))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          setState({ loading: false, me: null });
        } else {
          setState({ loading: false, me: null });
        }
      });
  }, []);
  if (state.loading) return null;
  if (!state.me) return <Navigate to="/login" replace />;
  return <MeContext.Provider value={state.me}>{children}</MeContext.Provider>;
}

function AppShell() {
  const me = useMe();
  const navigate = useNavigate();
  async function logout() {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
    } catch {
      // best-effort
    }
    navigate('/login');
  }
  return (
    <div className="layout">
      <header className="topbar">
        <div className="brand">drop</div>
        <nav>
          <NavLink to="/" end>
            Dashboard
          </NavLink>
          {me.is_admin && <NavLink to="/users">Users</NavLink>}
          {me.is_admin && <NavLink to="/agent-tokens">Agent tokens</NavLink>}
          {me.is_admin && <NavLink to="/audit">Audit</NavLink>}
          <NavLink to="/password">Password</NavLink>
          <a onClick={logout} style={{ cursor: 'pointer' }}>
            {me.username} · logout
          </a>
        </nav>
      </header>
      <main className="main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/agent-tokens" element={<AgentTokensPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/password" element={<ChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
